import React, { createContext, useContext, useState, useEffect, useLayoutEffect } from 'react';
import userSettingsService from '../services/userSettings.service';
import { isSafeFontStack, MIN_FONT_PX, MAX_FONT_PX } from '../theme/resolve';

// HSL-based color used by zebra stripes and gridline color pickers
export interface ZebraColor {
  hue: number;        // 0-360
  saturation: number; // 0-100  ("Intensity" slider)
  darkness: number;   // 0-100  (0 = baseline lightness, 100 = ~5% lightness)
  // Optional: font lightness override for cells inside zebra rows.
  // 0 = black, 100 = white. When undefined, falls back to mode default
  // (~10 light / ~90 dark) which matches Tailwind's default text color.
  fontLightness?: number;
}

// Default neutral gray
export const DEFAULT_ZEBRA_COLOR: ZebraColor = { hue: 220, saturation: 0, darkness: 0 };
export const DEFAULT_GRIDLINE_COLOR: ZebraColor = { hue: 220, saturation: 0, darkness: 0 };
export const DEFAULT_GROUP_ROW_COLOR: ZebraColor = { hue: 220, saturation: 8, darkness: 0 };
// Locked-field text colors ride the same ZebraColor shape, but off a baseline of
// 100 so `darkness` spans the full white→near-black range (a text color needs
// to go both ways, unlike a stripe tint that only darkens from its baseline).
export const LOCKED_TEXT_BASELINE_LIGHTNESS = 100;
// ~gray-900 in light mode, ~gray-100 in dark: the current disabled-text colors,
// so turning the option on changes nothing until a color is actually picked.
export const DEFAULT_LOCKED_TEXT_COLOR_LIGHT: ZebraColor = { hue: 220, saturation: 0, darkness: 95 };
export const DEFAULT_LOCKED_TEXT_COLOR_DARK: ZebraColor = { hue: 220, saturation: 0, darkness: 10 };
// Locked-field box (background + border) opacity, 0-100. 100 = untouched.
export const DEFAULT_LOCKED_FIELD_CHROME_OPACITY = 100;
// Input box background tints. A tint sits behind the value, so like the zebra
// stripes it stays close to the surface it replaces: near-white in light mode,
// near-black in dark. `darkness` then dials it further from that baseline.
export const FIELD_TINT_BASELINE_LIGHT = 97;
export const FIELD_TINT_BASELINE_DARK = 20;
export const FIELD_TINT_SATURATION_SCALE = 0.6; // subtle on a filled box, as with stripes
export const DEFAULT_FIELD_TINT_LIGHT: ZebraColor = { hue: 220, saturation: 12, darkness: 0 };
export const DEFAULT_FIELD_TINT_DARK: ZebraColor = { hue: 220, saturation: 12, darkness: 0 };

/* ---------------------------------------------------------------------------
   Font preferences (the "Font" section of Display Preferences)

   These drive the same four CSS vars the theme engine's typography block does
   (--app-font-family, --app-font-size, --fw-*), but as a per-user preference
   rather than part of the org theme — the theme engine's `enabled` flag is an
   org-level kill switch that fails closed, so routing a user's font through it
   would leave this section inert until an org theme is published.

   Emitted as a <style> element rather than inline styles on <html>:
   `:root:root` out-specifies the theme engine's plain `:root` rule so a
   user's own font beats the org default, without touching <html>'s inline
   style — so nothing else that writes inline vars there can wipe these, and
   these can't wipe anything else.
   --------------------------------------------------------------------------- */
export type AppTextCase = 'original' | 'uppercase' | 'title';
const USER_TEXT_CASE_STYLE_ID = 'user-text-case-preferences';
function normalizeTextCase(value: unknown): AppTextCase {
  return value === 'uppercase' || value === 'title' ? value : 'original';
}

export const USER_FONT_STYLE_ID = 'user-font-preferences';
export const FONT_WEIGHT_KEYS = ['normal', 'medium', 'semibold', 'bold'] as const;
export type FontWeightKey = typeof FONT_WEIGHT_KEYS[number];
export type FontWeights = Partial<Record<FontWeightKey, number>>;
/** Default cut for each step, shown by the sliders until one is overridden. */
export const DEFAULT_FONT_WEIGHTS: Record<FontWeightKey, number> = {
  normal: 400, medium: 500, semibold: 600, bold: 700,
};
export const MIN_FONT_WEIGHT = 100;
export const MAX_FONT_WEIGHT = 900;
const WEIGHT_VAR: Record<FontWeightKey, string> = {
  normal: '--fw-normal', medium: '--fw-medium', semibold: '--fw-semibold', bold: '--fw-bold',
};

export function clampFontWeight(value: number): number {
  return Math.max(MIN_FONT_WEIGHT, Math.min(MAX_FONT_WEIGHT, Math.round(value / 100) * 100));
}

/** Keep only the known weight keys holding a usable number. */
export function normalizeFontWeights(value: unknown): FontWeights {
  if (!value || typeof value !== 'object') return {};
  const out: FontWeights = {};
  for (const key of FONT_WEIGHT_KEYS) {
    const raw = (value as Record<string, unknown>)[key];
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''));
    if (Number.isFinite(n)) out[key] = clampFontWeight(n);
  }
  return out;
}

/**
 * Write (or remove) the user's font stylesheet. Only overridden properties are
 * emitted, so anything left alone keeps following the site default.
 */
function applyUserFontCss(
  family: string | null,
  sizePx: number | null,
  weights: FontWeights,
): void {
  if (typeof document === 'undefined') return;
  const declarations: string[] = [];
  // Same gate the org theme's font goes through — a stack is interpolated into
  // a stylesheet, so it must not be able to close the declaration.
  if (family && isSafeFontStack(family)) {
    declarations.push(`  --app-font-family: ${family.trim()};`);
  }
  if (sizePx !== null && Number.isFinite(sizePx)) {
    declarations.push(`  --app-font-size: ${Math.max(MIN_FONT_PX, Math.min(MAX_FONT_PX, Math.round(sizePx)))}px;`);
  }
  for (const key of FONT_WEIGHT_KEYS) {
    const weight = weights[key];
    if (typeof weight === 'number' && Number.isFinite(weight)) {
      declarations.push(`  ${WEIGHT_VAR[key]}: ${clampFontWeight(weight)};`);
    }
  }

  const existing = document.getElementById(USER_FONT_STYLE_ID) as HTMLStyleElement | null;
  if (declarations.length === 0) {
    existing?.remove();   // nothing overridden: no stub element left behind
    return;
  }

  const css = `:root:root {\n${declarations.join('\n')}\n}`;
  const el = existing ?? document.createElement('style');
  if (!existing) {
    el.id = USER_FONT_STYLE_ID;
    document.head.appendChild(el);
  }
  if (el.textContent !== css) el.textContent = css;
}

/** 0-100, falling back to the default for anything unparseable. */
export function normalizeOpacityPercent(value: unknown, fallback = DEFAULT_LOCKED_FIELD_CHROME_OPACITY): number {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(100, Math.round(n)));
}
const GROUP_ROW_BASELINE_LIGHT = 36;
const GROUP_ROW_BASELINE_DARK = 28;
const GROUP_ROW_DEPTH_STEP = 9;
const GROUP_ROW_DEPTHS = 4;
const DEFAULT_FONT_LIGHTNESS_LIGHT = 10; // ~ Tailwind text-gray-900
const DEFAULT_FONT_LIGHTNESS_DARK = 90;  // ~ Tailwind text-gray-100

// Baseline lightness anchors used when darkness = 0
const STRIPE_BASELINE_LIGHT = 94; // matches existing light-mode stripe
const STRIPE_BASELINE_DARK = 18;  // matches existing dark-mode stripe
const GRIDLINE_BASELINE_LIGHT = 90; // ~gray-200, matches --theme-border-primary in light mode
const GRIDLINE_BASELINE_DARK = 34;  // ~gray-600, matches --theme-border-primary in dark mode
const DARKNESS_MIN_LIGHTNESS = 5;   // floor when darkness = 100

// Coerce loaded values into a complete ZebraColor (back-compat for saved prefs without darkness)
export function normalizeZebraColor(value: unknown): ZebraColor {
  if (value && typeof value === 'object') {
    const v = value as Partial<ZebraColor>;
    return {
      hue: typeof v.hue === 'number' ? v.hue : DEFAULT_ZEBRA_COLOR.hue,
      saturation: typeof v.saturation === 'number' ? v.saturation : DEFAULT_ZEBRA_COLOR.saturation,
      darkness: typeof v.darkness === 'number' ? v.darkness : 0,
      fontLightness: typeof v.fontLightness === 'number' ? v.fontLightness : undefined,
    };
  }
  return DEFAULT_ZEBRA_COLOR;
}

function effectiveLightness(darkness: number, baseline: number): number {
  const d = Math.max(0, Math.min(100, darkness));
  return baseline - (baseline - DARKNESS_MIN_LIGHTNESS) * (d / 100);
}

// Convert HSL to RGB string for CSS
function hslToRgb(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color);
  };
  return `${f(0)} ${f(8)} ${f(4)}`;
}

// Generate zebra stripe CSS color from ZebraColor
function generateZebraRgb(color: ZebraColor, isDark: boolean): string {
  const { hue, saturation, darkness } = color;
  const sat = saturation * 0.6; // Allow up to 60% saturation for zebra stripes
  const baseline = isDark ? STRIPE_BASELINE_DARK : STRIPE_BASELINE_LIGHT;
  return hslToRgb(hue, sat, effectiveLightness(darkness, baseline));
}

// Generate gridline CSS color from ZebraColor (full saturation range — lines are thin)
function generateGridlineRgb(color: ZebraColor, isDark: boolean): string {
  const { hue, saturation, darkness } = color;
  const baseline = isDark ? GRIDLINE_BASELINE_DARK : GRIDLINE_BASELINE_LIGHT;
  return hslToRgb(hue, saturation, effectiveLightness(darkness, baseline));
}

// Generate font color (neutral gray at the chosen lightness) for stripe rows
function generateFontRgb(fontLightness: number): string {
  return hslToRgb(0, 0, Math.max(0, Math.min(100, fontLightness)));
}

// Apply all display CSS variables to document root
function applyDisplayCssVars(
  zebraLight: ZebraColor,
  zebraDark: ZebraColor,
  gridLight: ZebraColor,
  gridDark: ZebraColor,
  groupLight: ZebraColor,
  groupDark: ZebraColor,
) {
  const root = document.documentElement;
  root.style.setProperty('--zebra-stripe-light', generateZebraRgb(zebraLight, false));
  root.style.setProperty('--zebra-stripe-dark', generateZebraRgb(zebraDark, true));
  root.style.setProperty('--gridline-color-light', generateGridlineRgb(gridLight, false));
  root.style.setProperty('--gridline-color-dark', generateGridlineRgb(gridDark, true));
  root.style.setProperty(
    '--zebra-font-light',
    generateFontRgb(zebraLight.fontLightness ?? DEFAULT_FONT_LIGHTNESS_LIGHT),
  );
  root.style.setProperty(
    '--zebra-font-dark',
    generateFontRgb(zebraDark.fontLightness ?? DEFAULT_FONT_LIGHTNESS_DARK),
  );
  applyGroupRowCssVars(groupLight, groupDark);
}

// Locked (disabled / read-only) field text: color per mode + weight/style.
// Published as vars unconditionally; the `styled-locked-fields` root class is
// what decides whether the rules in index.css consume them.
interface FieldDisplayVars {
  textColorLight: ZebraColor;
  textColorDark: ZebraColor;
  bold: boolean;
  italic: boolean;
  chromeOpacity: number;
  editableTintLight: ZebraColor;
  editableTintDark: ZebraColor;
  lockedTintLight: ZebraColor;
  lockedTintDark: ZebraColor;
}

// One RGB triplet per tint, per mode — `rgb(var(--x) / <alpha>)` in index.css,
// matching how the theme's own surface tokens are published.
function tintRgb(color: ZebraColor, isDark: boolean): string {
  const baseline = isDark ? FIELD_TINT_BASELINE_DARK : FIELD_TINT_BASELINE_LIGHT;
  return hslToRgb(
    color.hue,
    color.saturation * FIELD_TINT_SATURATION_SCALE,
    effectiveLightness(color.darkness, baseline),
  );
}

function applyLockedFieldCssVars({
  textColorLight,
  textColorDark,
  bold,
  italic,
  chromeOpacity,
  editableTintLight,
  editableTintDark,
  lockedTintLight,
  lockedTintDark,
}: FieldDisplayVars) {
  const root = document.documentElement;
  const rgbFor = (c: ZebraColor) =>
    hslToRgb(c.hue, c.saturation, effectiveLightness(c.darkness, LOCKED_TEXT_BASELINE_LIGHTNESS));
  root.style.setProperty('--locked-field-text-light', rgbFor(textColorLight));
  root.style.setProperty('--locked-field-text-dark', rgbFor(textColorDark));
  root.style.setProperty('--editable-field-tint-light', tintRgb(editableTintLight, false));
  root.style.setProperty('--editable-field-tint-dark', tintRgb(editableTintDark, true));
  root.style.setProperty('--locked-field-tint-light', tintRgb(lockedTintLight, false));
  root.style.setProperty('--locked-field-tint-dark', tintRgb(lockedTintDark, true));
  // `inherit` leaves the field's own weight/style alone when the user hasn't
  // asked for bold/italic — the option should only add what was requested.
  root.style.setProperty('--locked-field-font-weight', bold ? '700' : 'inherit');
  root.style.setProperty('--locked-field-font-style', italic ? 'italic' : 'inherit');
  // Alpha for the box's background + border colors. NOT element `opacity`: that
  // fades an element together with its children, which would take the value's
  // text down with the box — the exact thing this option must not do.
  root.style.setProperty('--locked-field-chrome-alpha', String(normalizeOpacityPercent(chromeOpacity) / 100));
}

function applyGroupRowCssVars(colorLight: ZebraColor, colorDark: ZebraColor) {
  const root = document.documentElement;
  for (let depth = 0; depth < GROUP_ROW_DEPTHS; depth++) {
    const lLight = Math.min(95, effectiveLightness(colorLight.darkness, GROUP_ROW_BASELINE_LIGHT) + depth * GROUP_ROW_DEPTH_STEP);
    const lDark  = Math.min(95, effectiveLightness(colorDark.darkness,  GROUP_ROW_BASELINE_DARK)  + depth * GROUP_ROW_DEPTH_STEP);
    root.style.setProperty(`--group-row-light-${depth}`, hslToRgb(colorLight.hue, colorLight.saturation, lLight));
    root.style.setProperty(`--group-row-dark-${depth}`,  hslToRgb(colorDark.hue,  colorDark.saturation,  lDark));
    const textLight = lLight < 55 ? '255 255 255' : '17 24 39';
    const textDark  = lDark  < 55 ? '255 255 255' : '17 24 39';
    root.style.setProperty(`--group-row-text-light-${depth}`, textLight);
    root.style.setProperty(`--group-row-text-dark-${depth}`,  textDark);
    const subtextLight = lLight < 55 ? '209 213 219' : '107 114 128';
    const subtextDark  = lDark  < 55 ? '209 213 219' : '107 114 128';
    root.style.setProperty(`--group-row-subtext-light-${depth}`, subtextLight);
    root.style.setProperty(`--group-row-subtext-dark-${depth}`,  subtextDark);
  }
}

interface DisplayPreferencesContextType {
  showTableGridlines: boolean;
  setShowTableGridlines: (enabled: boolean) => void;
  showHorizontalGridlines: boolean;
  setShowHorizontalGridlines: (enabled: boolean) => void;
  showVerticalGridlines: boolean;
  setShowVerticalGridlines: (enabled: boolean) => void;
  showTableZebraStriping: boolean;
  setShowTableZebraStriping: (enabled: boolean) => void;
  zebraColorLight: ZebraColor;
  setZebraColorLight: (color: ZebraColor) => void;
  zebraColorDark: ZebraColor;
  setZebraColorDark: (color: ZebraColor) => void;
  gridlineColorLight: ZebraColor;
  setGridlineColorLight: (color: ZebraColor) => void;
  gridlineColorDark: ZebraColor;
  setGridlineColorDark: (color: ZebraColor) => void;
  groupRowColorLight: ZebraColor;
  setGroupRowColorLight: (color: ZebraColor) => void;
  groupRowColorDark: ZebraColor;
  setGroupRowColorDark: (color: ZebraColor) => void;
  getZebraStyle: () => React.CSSProperties;
  getZebraClass: () => string;
  isLoading: boolean;
  // Debug settings
  showColumnWidths: boolean;
  setShowColumnWidths: (enabled: boolean) => void;
  showGridHighlight: boolean;
  setShowGridHighlight: (enabled: boolean) => void;
  showGridLabels: boolean;
  setShowGridLabels: (enabled: boolean) => void;
  showGridBorders: boolean;
  setShowGridBorders: (enabled: boolean) => void;
  // Compact table mode
  compactTableMode: boolean;
  setCompactTableMode: (enabled: boolean) => void;
  // Compact order details drawer
  compactOrderDetails: boolean;
  setCompactOrderDetails: (enabled: boolean) => void;
  // Full-Slide nav: hovering the collapsed sidebar slides the whole nav out
  navFullSlide: boolean;
  setNavFullSlide: (enabled: boolean) => void;
  // Placeholder text inside form inputs (hidden app-wide when false)
  showInputPlaceholders: boolean;
  setShowInputPlaceholders: (enabled: boolean) => void;
  // Locked (disabled / read-only) fields render as plain text, no input box
  plainTextLockedFields: boolean;
  setPlainTextLockedFields: (enabled: boolean) => void;
  // Locked-field text styling: own color per mode, plus bold / italic
  styleLockedFieldText: boolean;
  setStyleLockedFieldText: (enabled: boolean) => void;
  lockedFieldTextColorLight: ZebraColor;
  setLockedFieldTextColorLight: (color: ZebraColor) => void;
  lockedFieldTextColorDark: ZebraColor;
  setLockedFieldTextColorDark: (color: ZebraColor) => void;
  lockedFieldTextBold: boolean;
  setLockedFieldTextBold: (enabled: boolean) => void;
  lockedFieldTextItalic: boolean;
  setLockedFieldTextItalic: (enabled: boolean) => void;
  /** 0-100. Fades the locked field's box only; its value stays full strength. */
  lockedFieldChromeOpacity: number;
  setLockedFieldChromeOpacity: (percent: number) => void;
  // Input box background tints — editable and locked fields tint separately, so
  // the two states can be told apart by color.
  tintEditableFields: boolean;
  setTintEditableFields: (enabled: boolean) => void;
  editableFieldTintLight: ZebraColor;
  setEditableFieldTintLight: (color: ZebraColor) => void;
  editableFieldTintDark: ZebraColor;
  setEditableFieldTintDark: (color: ZebraColor) => void;
  tintLockedFields: boolean;
  setTintLockedFields: (enabled: boolean) => void;
  // Font section. null / {} means "follow the site default" — nothing emitted.
  appTextCase: AppTextCase;
  setAppTextCase: (value: AppTextCase) => void;
  appFontFamily: string | null;
  setAppFontFamily: (stack: string | null) => void;
  appFontSizePx: number | null;
  setAppFontSizePx: (px: number | null) => void;
  appFontWeights: FontWeights;
  setAppFontWeight: (key: FontWeightKey, weight: number | null) => void;
  /** All three at once, one server write — what the Font section's Save does. */
  setAppFont: (next: { family: string | null; sizePx: number | null; weights: FontWeights }) => void;
  lockedFieldTintLight: ZebraColor;
  setLockedFieldTintLight: (color: ZebraColor) => void;
  lockedFieldTintDark: ZebraColor;
  setLockedFieldTintDark: (color: ZebraColor) => void;
  // Field name visibility (for LLM help integration)
  showFieldNames: boolean;
  setShowFieldNames: (enabled: boolean) => void;
  highlightedFieldId: string | null;
  setHighlightedFieldId: (fieldId: string | null) => void;
  // Global TanTable display modes
  globalSuperCompact: boolean;
  setGlobalSuperCompact: (enabled: boolean) => void;
  globalFitToWidth: boolean;
  setGlobalFitToWidth: (enabled: boolean) => void;
  globalShowColumnFilters: boolean;
  setGlobalShowColumnFilters: (enabled: boolean) => void;
}

const DisplayPreferencesContext = createContext<DisplayPreferencesContextType | undefined>(undefined);

export const DisplayPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showTableGridlines, setShowTableGridlinesState] = useState(() => {
    const saved = localStorage.getItem('showTableGridlines');
    return saved === 'true';
  });
  const [showHorizontalGridlines, setShowHorizontalGridlinesState] = useState(() => {
    const saved = localStorage.getItem('showHorizontalGridlines');
    return saved !== 'false'; // Default true
  });
  const [showVerticalGridlines, setShowVerticalGridlinesState] = useState(() => {
    const saved = localStorage.getItem('showVerticalGridlines');
    return saved !== 'false'; // Default true
  });
  const [showTableZebraStriping, setShowTableZebraStripingState] = useState(() => {
    const saved = localStorage.getItem('showTableZebraStriping');
    return saved === 'true';
  });
  const [zebraColorLight, setZebraColorLightState] = useState<ZebraColor>(() => {
    const saved = localStorage.getItem('zebraColorLight');
    if (saved) {
      try {
        return normalizeZebraColor(JSON.parse(saved));
      } catch {
        return DEFAULT_ZEBRA_COLOR;
      }
    }
    return DEFAULT_ZEBRA_COLOR;
  });
  const [zebraColorDark, setZebraColorDarkState] = useState<ZebraColor>(() => {
    const saved = localStorage.getItem('zebraColorDark');
    if (saved) {
      try {
        return normalizeZebraColor(JSON.parse(saved));
      } catch {
        return DEFAULT_ZEBRA_COLOR;
      }
    }
    return DEFAULT_ZEBRA_COLOR;
  });
  const [gridlineColorLight, setGridlineColorLightState] = useState<ZebraColor>(() => {
    const saved = localStorage.getItem('gridlineColorLight');
    if (saved) {
      try {
        return normalizeZebraColor(JSON.parse(saved));
      } catch {
        return DEFAULT_GRIDLINE_COLOR;
      }
    }
    return DEFAULT_GRIDLINE_COLOR;
  });
  const [gridlineColorDark, setGridlineColorDarkState] = useState<ZebraColor>(() => {
    const saved = localStorage.getItem('gridlineColorDark');
    if (saved) {
      try {
        return normalizeZebraColor(JSON.parse(saved));
      } catch {
        return DEFAULT_GRIDLINE_COLOR;
      }
    }
    return DEFAULT_GRIDLINE_COLOR;
  });
  const [groupRowColorLight, setGroupRowColorLightState] = useState<ZebraColor>(() => {
    const saved = localStorage.getItem('groupRowColorLight');
    if (saved) { try { return normalizeZebraColor(JSON.parse(saved)); } catch { return DEFAULT_GROUP_ROW_COLOR; } }
    return DEFAULT_GROUP_ROW_COLOR;
  });
  const [groupRowColorDark, setGroupRowColorDarkState] = useState<ZebraColor>(() => {
    const saved = localStorage.getItem('groupRowColorDark');
    if (saved) { try { return normalizeZebraColor(JSON.parse(saved)); } catch { return DEFAULT_GROUP_ROW_COLOR; } }
    return DEFAULT_GROUP_ROW_COLOR;
  });
  const [showColumnWidths, setShowColumnWidthsState] = useState(() => {
    const saved = localStorage.getItem('showColumnWidths');
    return saved === 'true';
  });
  const [showGridHighlight, setShowGridHighlightState] = useState(() => {
    const saved = localStorage.getItem('showGridHighlight');
    return saved === 'true';
  });
  const [showGridLabels, setShowGridLabelsState] = useState(() => {
    const saved = localStorage.getItem('showGridLabels');
    return saved === 'true';
  });
  const [showGridBorders, setShowGridBordersState] = useState(() => {
    const saved = localStorage.getItem('showGridBorders');
    return saved === 'true';
  });
  const [compactTableMode, setCompactTableModeState] = useState(() => {
    const saved = localStorage.getItem('compactTableMode');
    return saved !== 'false'; // Default to true
  });
  const [compactOrderDetails, setCompactOrderDetailsState] = useState(() => {
    const saved = localStorage.getItem('compactOrderDetails');
    return saved === 'true';
  });
  const [navFullSlide, setNavFullSlideState] = useState(() => {
    const saved = localStorage.getItem('navFullSlide');
    return saved === 'true';
  });
  const [showInputPlaceholders, setShowInputPlaceholdersState] = useState(() => {
    const saved = localStorage.getItem('showInputPlaceholders');
    return saved !== 'false'; // Default true — placeholders visible
  });
  const [plainTextLockedFields, setPlainTextLockedFieldsState] = useState(() => {
    const saved = localStorage.getItem('plainTextLockedFields');
    return saved === 'true'; // Default false — locked fields keep their input box
  });
  const [styleLockedFieldText, setStyleLockedFieldTextState] = useState(() => {
    return localStorage.getItem('styleLockedFieldText') === 'true';
  });
  const [lockedFieldTextColorLight, setLockedFieldTextColorLightState] = useState<ZebraColor>(() => {
    const saved = localStorage.getItem('lockedFieldTextColorLight');
    if (saved) { try { return normalizeZebraColor(JSON.parse(saved)); } catch { return DEFAULT_LOCKED_TEXT_COLOR_LIGHT; } }
    return DEFAULT_LOCKED_TEXT_COLOR_LIGHT;
  });
  const [lockedFieldTextColorDark, setLockedFieldTextColorDarkState] = useState<ZebraColor>(() => {
    const saved = localStorage.getItem('lockedFieldTextColorDark');
    if (saved) { try { return normalizeZebraColor(JSON.parse(saved)); } catch { return DEFAULT_LOCKED_TEXT_COLOR_DARK; } }
    return DEFAULT_LOCKED_TEXT_COLOR_DARK;
  });
  const [lockedFieldTextBold, setLockedFieldTextBoldState] = useState(() => {
    return localStorage.getItem('lockedFieldTextBold') === 'true';
  });
  const [lockedFieldTextItalic, setLockedFieldTextItalicState] = useState(() => {
    return localStorage.getItem('lockedFieldTextItalic') === 'true';
  });
  const [lockedFieldChromeOpacity, setLockedFieldChromeOpacityState] = useState(() => {
    const saved = localStorage.getItem('lockedFieldChromeOpacity');
    return saved === null ? DEFAULT_LOCKED_FIELD_CHROME_OPACITY : normalizeOpacityPercent(saved);
  });
  const readTint = (key: string, fallback: ZebraColor): ZebraColor => {
    const saved = localStorage.getItem(key);
    if (saved) { try { return normalizeZebraColor(JSON.parse(saved)); } catch { return fallback; } }
    return fallback;
  };
  const [tintEditableFields, setTintEditableFieldsState] = useState(() => {
    return localStorage.getItem('tintEditableFields') === 'true';
  });
  const [editableFieldTintLight, setEditableFieldTintLightState] = useState<ZebraColor>(
    () => readTint('editableFieldTintLight', DEFAULT_FIELD_TINT_LIGHT),
  );
  const [editableFieldTintDark, setEditableFieldTintDarkState] = useState<ZebraColor>(
    () => readTint('editableFieldTintDark', DEFAULT_FIELD_TINT_DARK),
  );
  const [tintLockedFields, setTintLockedFieldsState] = useState(() => {
    return localStorage.getItem('tintLockedFields') === 'true';
  });
  const [lockedFieldTintLight, setLockedFieldTintLightState] = useState<ZebraColor>(
    () => readTint('lockedFieldTintLight', DEFAULT_FIELD_TINT_LIGHT),
  );
  const [lockedFieldTintDark, setLockedFieldTintDarkState] = useState<ZebraColor>(
    () => readTint('lockedFieldTintDark', DEFAULT_FIELD_TINT_DARK),
  );
  const [appTextCase, setAppTextCaseState] = useState<AppTextCase>(() =>
    normalizeTextCase(localStorage.getItem('appTextCase')),
  );
  const [appFontFamily, setAppFontFamilyState] = useState<string | null>(() => {
    return localStorage.getItem('appFontFamily') || null;
  });
  const [appFontSizePx, setAppFontSizePxState] = useState<number | null>(() => {
    const saved = parseFloat(localStorage.getItem('appFontSizePx') ?? '');
    return Number.isFinite(saved) ? saved : null;
  });
  const [appFontWeights, setAppFontWeightsState] = useState<FontWeights>(() => {
    const saved = localStorage.getItem('appFontWeights');
    if (saved) { try { return normalizeFontWeights(JSON.parse(saved)); } catch { return {}; } }
    return {};
  });
  const [showFieldNames, setShowFieldNamesState] = useState(() => {
    const saved = localStorage.getItem('showFieldNames');
    return saved === 'true';
  });
  const [highlightedFieldId, setHighlightedFieldIdState] = useState<string | null>(null);
  const [globalSuperCompact, setGlobalSuperCompactState] = useState(() => {
    const saved = localStorage.getItem('globalSuperCompact');
    return saved === 'true';
  });
  const [globalFitToWidth, setGlobalFitToWidthState] = useState(() => {
    const saved = localStorage.getItem('globalFitToWidth');
    return saved === 'true';
  });
  const [globalShowColumnFilters, setGlobalShowColumnFiltersState] = useState(() => {
    const saved = localStorage.getItem('globalShowColumnFilters');
    return saved === 'true';
  });
  const [isLoading, setIsLoading] = useState(true);

  // Apply CSS variables when colors change
  useEffect(() => {
    applyDisplayCssVars(zebraColorLight, zebraColorDark, gridlineColorLight, gridlineColorDark, groupRowColorLight, groupRowColorDark);
  }, [zebraColorLight, zebraColorDark, gridlineColorLight, gridlineColorDark, groupRowColorLight, groupRowColorDark]);

  // Hiding placeholders is a single root-level class (see index.css) so every
  // native input/textarea in the app is covered without threading a prop.
  useEffect(() => {
    document.documentElement.classList.toggle('hide-input-placeholders', !showInputPlaceholders);
  }, [showInputPlaceholders]);

  // Locked-field flattening: the root class carries the native-input rules (see
  // index.css); shared components whose box lives on a wrapper element tag it
  // with FIELD_SHELL_LOCKED_CLASS themselves via useFieldShellDisplay().
  useEffect(() => {
    document.documentElement.classList.toggle('plain-locked-fields', plainTextLockedFields);
  }, [plainTextLockedFields]);

  useEffect(() => {
    document.documentElement.classList.toggle('styled-locked-fields', styleLockedFieldText);
  }, [styleLockedFieldText]);

  // A full-opacity box is the untouched look, so the class only goes on when the
  // slider has actually been pulled back — no rules to evaluate otherwise.
  useEffect(() => {
    document.documentElement.classList.toggle('faded-locked-fields', lockedFieldChromeOpacity < 100);
  }, [lockedFieldChromeOpacity]);

  // CSS only: never rewrite React text nodes, input values, or exported data.
  // Cover portals and explicit component text-transform rules as well as body text.
  useLayoutEffect(() => {
    if (appTextCase === 'original') return;
    const style = document.createElement('style');
    style.id = USER_TEXT_CASE_STYLE_ID;
    const transform = appTextCase === 'uppercase' ? 'uppercase' : 'capitalize';
    style.textContent = `html:root body, html:root body *, html:root body *::placeholder {
      text-transform: ${transform} !important;
    }`;
    document.head.appendChild(style);
    return () => style.remove();
  }, [appTextCase]);

  // useLayoutEffect, not useEffect: the cached preference is read synchronously
  // in the state initializer, so writing it before paint means a reload shows
  // the user's font from the first frame instead of flashing the site default.
  useLayoutEffect(() => {
    applyUserFontCss(appFontFamily, appFontSizePx, appFontWeights);
  }, [appFontFamily, appFontSizePx, appFontWeights]);

  useEffect(() => {
    document.documentElement.classList.toggle('tint-editable-fields', tintEditableFields);
  }, [tintEditableFields]);

  useEffect(() => {
    document.documentElement.classList.toggle('tint-locked-fields', tintLockedFields);
  }, [tintLockedFields]);

  useEffect(() => {
    applyLockedFieldCssVars({
      textColorLight: lockedFieldTextColorLight,
      textColorDark: lockedFieldTextColorDark,
      bold: lockedFieldTextBold,
      italic: lockedFieldTextItalic,
      chromeOpacity: lockedFieldChromeOpacity,
      editableTintLight: editableFieldTintLight,
      editableTintDark: editableFieldTintDark,
      lockedTintLight: lockedFieldTintLight,
      lockedTintDark: lockedFieldTintDark,
    });
  }, [
    lockedFieldTextColorLight, lockedFieldTextColorDark, lockedFieldTextBold, lockedFieldTextItalic,
    lockedFieldChromeOpacity, editableFieldTintLight, editableFieldTintDark,
    lockedFieldTintLight, lockedFieldTintDark,
  ]);

  // Helper to get all current preferences
  const getCurrentPrefs = () => ({
    showTableGridlines,
    showHorizontalGridlines,
    showVerticalGridlines,
    showTableZebraStriping,
    zebraColorLight,
    zebraColorDark,
    gridlineColorLight,
    gridlineColorDark,
    groupRowColorLight,
    groupRowColorDark,
    showColumnWidths,
    showGridHighlight,
    showGridLabels,
    showGridBorders,
    compactTableMode,
    compactOrderDetails,
    navFullSlide,
    showInputPlaceholders,
    plainTextLockedFields,
    styleLockedFieldText,
    lockedFieldTextColorLight,
    lockedFieldTextColorDark,
    lockedFieldTextBold,
    lockedFieldTextItalic,
    lockedFieldChromeOpacity,
    tintEditableFields,
    editableFieldTintLight,
    editableFieldTintDark,
    tintLockedFields,
    lockedFieldTintLight,
    lockedFieldTintDark,
    appTextCase,
    appFontFamily,
    appFontSizePx,
    appFontWeights,
    showFieldNames,
    globalSuperCompact,
    globalFitToWidth,
    globalShowColumnFilters,
  });

  // Load preferences from database on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('display_preferences');
        if (setting && setting.setting_value) {
          const prefs = typeof setting.setting_value === 'string'
            ? JSON.parse(setting.setting_value)
            : setting.setting_value;

          if (prefs.showTableGridlines !== undefined) {
            setShowTableGridlinesState(prefs.showTableGridlines);
            localStorage.setItem('showTableGridlines', prefs.showTableGridlines.toString());
          }
          if (prefs.showHorizontalGridlines !== undefined) {
            setShowHorizontalGridlinesState(prefs.showHorizontalGridlines);
            localStorage.setItem('showHorizontalGridlines', prefs.showHorizontalGridlines.toString());
          }
          if (prefs.showVerticalGridlines !== undefined) {
            setShowVerticalGridlinesState(prefs.showVerticalGridlines);
            localStorage.setItem('showVerticalGridlines', prefs.showVerticalGridlines.toString());
          }
          if (prefs.showTableZebraStriping !== undefined) {
            setShowTableZebraStripingState(prefs.showTableZebraStriping);
            localStorage.setItem('showTableZebraStriping', prefs.showTableZebraStriping.toString());
          }
          // Handle ZebraColor format (normalized for back-compat with prefs missing `darkness`)
          if (prefs.zebraColorLight !== undefined) {
            const lightColor = normalizeZebraColor(prefs.zebraColorLight);
            setZebraColorLightState(lightColor);
            localStorage.setItem('zebraColorLight', JSON.stringify(lightColor));
          }
          if (prefs.zebraColorDark !== undefined) {
            const darkColor = normalizeZebraColor(prefs.zebraColorDark);
            setZebraColorDarkState(darkColor);
            localStorage.setItem('zebraColorDark', JSON.stringify(darkColor));
          }
          if (prefs.gridlineColorLight !== undefined) {
            const lightColor = normalizeZebraColor(prefs.gridlineColorLight);
            setGridlineColorLightState(lightColor);
            localStorage.setItem('gridlineColorLight', JSON.stringify(lightColor));
          }
          if (prefs.gridlineColorDark !== undefined) {
            const darkColor = normalizeZebraColor(prefs.gridlineColorDark);
            setGridlineColorDarkState(darkColor);
            localStorage.setItem('gridlineColorDark', JSON.stringify(darkColor));
          }
          if (prefs.groupRowColorLight !== undefined) {
            const c = normalizeZebraColor(prefs.groupRowColorLight);
            setGroupRowColorLightState(c);
            localStorage.setItem('groupRowColorLight', JSON.stringify(c));
          }
          if (prefs.groupRowColorDark !== undefined) {
            const c = normalizeZebraColor(prefs.groupRowColorDark);
            setGroupRowColorDarkState(c);
            localStorage.setItem('groupRowColorDark', JSON.stringify(c));
          }
          if (prefs.showColumnWidths !== undefined) {
            setShowColumnWidthsState(prefs.showColumnWidths);
            localStorage.setItem('showColumnWidths', prefs.showColumnWidths.toString());
          }
          if (prefs.showGridHighlight !== undefined) {
            setShowGridHighlightState(prefs.showGridHighlight);
            localStorage.setItem('showGridHighlight', prefs.showGridHighlight.toString());
          }
          if (prefs.showGridLabels !== undefined) {
            setShowGridLabelsState(prefs.showGridLabels);
            localStorage.setItem('showGridLabels', prefs.showGridLabels.toString());
          }
          if (prefs.showGridBorders !== undefined) {
            setShowGridBordersState(prefs.showGridBorders);
            localStorage.setItem('showGridBorders', prefs.showGridBorders.toString());
          }
          if (prefs.compactTableMode !== undefined) {
            setCompactTableModeState(prefs.compactTableMode);
            localStorage.setItem('compactTableMode', prefs.compactTableMode.toString());
          }
          if (prefs.compactOrderDetails !== undefined) {
            setCompactOrderDetailsState(prefs.compactOrderDetails);
            localStorage.setItem('compactOrderDetails', prefs.compactOrderDetails.toString());
          }
          if (prefs.navFullSlide !== undefined) {
            setNavFullSlideState(prefs.navFullSlide);
            localStorage.setItem('navFullSlide', prefs.navFullSlide.toString());
          }
          if (prefs.showInputPlaceholders !== undefined) {
            setShowInputPlaceholdersState(prefs.showInputPlaceholders);
            localStorage.setItem('showInputPlaceholders', prefs.showInputPlaceholders.toString());
          }
          if (prefs.plainTextLockedFields !== undefined) {
            setPlainTextLockedFieldsState(prefs.plainTextLockedFields);
            localStorage.setItem('plainTextLockedFields', prefs.plainTextLockedFields.toString());
          }
          if (prefs.styleLockedFieldText !== undefined) {
            setStyleLockedFieldTextState(prefs.styleLockedFieldText);
            localStorage.setItem('styleLockedFieldText', prefs.styleLockedFieldText.toString());
          }
          if (prefs.lockedFieldTextColorLight !== undefined) {
            const c = normalizeZebraColor(prefs.lockedFieldTextColorLight);
            setLockedFieldTextColorLightState(c);
            localStorage.setItem('lockedFieldTextColorLight', JSON.stringify(c));
          }
          if (prefs.lockedFieldTextColorDark !== undefined) {
            const c = normalizeZebraColor(prefs.lockedFieldTextColorDark);
            setLockedFieldTextColorDarkState(c);
            localStorage.setItem('lockedFieldTextColorDark', JSON.stringify(c));
          }
          if (prefs.lockedFieldTextBold !== undefined) {
            setLockedFieldTextBoldState(prefs.lockedFieldTextBold);
            localStorage.setItem('lockedFieldTextBold', prefs.lockedFieldTextBold.toString());
          }
          if (prefs.lockedFieldTextItalic !== undefined) {
            setLockedFieldTextItalicState(prefs.lockedFieldTextItalic);
            localStorage.setItem('lockedFieldTextItalic', prefs.lockedFieldTextItalic.toString());
          }
          if (prefs.lockedFieldChromeOpacity !== undefined) {
            const pct = normalizeOpacityPercent(prefs.lockedFieldChromeOpacity);
            setLockedFieldChromeOpacityState(pct);
            localStorage.setItem('lockedFieldChromeOpacity', String(pct));
          }
          if (prefs.tintEditableFields !== undefined) {
            setTintEditableFieldsState(prefs.tintEditableFields);
            localStorage.setItem('tintEditableFields', prefs.tintEditableFields.toString());
          }
          if (prefs.tintLockedFields !== undefined) {
            setTintLockedFieldsState(prefs.tintLockedFields);
            localStorage.setItem('tintLockedFields', prefs.tintLockedFields.toString());
          }
          ([
            ['editableFieldTintLight', setEditableFieldTintLightState],
            ['editableFieldTintDark', setEditableFieldTintDarkState],
            ['lockedFieldTintLight', setLockedFieldTintLightState],
            ['lockedFieldTintDark', setLockedFieldTintDarkState],
          ] as const).forEach(([key, setter]) => {
            if (prefs[key] !== undefined) {
              const c = normalizeZebraColor(prefs[key]);
              setter(c);
              localStorage.setItem(key, JSON.stringify(c));
            }
          });
          // Font: null is meaningful (an explicit "back to the site default"),
          // so only `undefined` — the key never having been saved — is skipped.
          const loadedTextCase = normalizeTextCase(prefs.appTextCase);
          setAppTextCaseState(loadedTextCase);
          localStorage.setItem('appTextCase', loadedTextCase);
          if (prefs.appFontFamily !== undefined) {
            const stack = typeof prefs.appFontFamily === 'string' && prefs.appFontFamily
              ? prefs.appFontFamily : null;
            setAppFontFamilyState(stack);
            if (stack) localStorage.setItem('appFontFamily', stack);
            else localStorage.removeItem('appFontFamily');
          }
          if (prefs.appFontSizePx !== undefined) {
            const px = Number.isFinite(prefs.appFontSizePx) ? Number(prefs.appFontSizePx) : null;
            setAppFontSizePxState(px);
            if (px === null) localStorage.removeItem('appFontSizePx');
            else localStorage.setItem('appFontSizePx', String(px));
          }
          if (prefs.appFontWeights !== undefined) {
            const weights = normalizeFontWeights(prefs.appFontWeights);
            setAppFontWeightsState(weights);
            localStorage.setItem('appFontWeights', JSON.stringify(weights));
          }
          if (prefs.showFieldNames !== undefined) {
            setShowFieldNamesState(prefs.showFieldNames);
            localStorage.setItem('showFieldNames', prefs.showFieldNames.toString());
          }
          if (prefs.globalSuperCompact !== undefined) {
            setGlobalSuperCompactState(prefs.globalSuperCompact);
            localStorage.setItem('globalSuperCompact', prefs.globalSuperCompact.toString());
          }
          if (prefs.globalFitToWidth !== undefined) {
            setGlobalFitToWidthState(prefs.globalFitToWidth);
            localStorage.setItem('globalFitToWidth', prefs.globalFitToWidth.toString());
          }
          if (prefs.globalShowColumnFilters !== undefined) {
            setGlobalShowColumnFiltersState(prefs.globalShowColumnFilters);
            localStorage.setItem('globalShowColumnFilters', prefs.globalShowColumnFilters.toString());
          }
        }
      } catch (error) {
        // Silently fail - use localStorage values
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, []);

  // Save preferences helper
  const savePreferences = async (prefs: {
    showTableGridlines: boolean;
    showHorizontalGridlines: boolean;
    showVerticalGridlines: boolean;
    showTableZebraStriping: boolean;
    zebraColorLight: ZebraColor;
    zebraColorDark: ZebraColor;
    gridlineColorLight: ZebraColor;
    gridlineColorDark: ZebraColor;
    groupRowColorLight: ZebraColor;
    groupRowColorDark: ZebraColor;
    showColumnWidths: boolean;
    showGridHighlight: boolean;
    showGridLabels: boolean;
    showGridBorders: boolean;
    compactTableMode: boolean;
    compactOrderDetails: boolean;
    navFullSlide: boolean;
    showInputPlaceholders: boolean;
    plainTextLockedFields: boolean;
    styleLockedFieldText: boolean;
    lockedFieldTextColorLight: ZebraColor;
    lockedFieldTextColorDark: ZebraColor;
    lockedFieldTextBold: boolean;
    lockedFieldTextItalic: boolean;
    lockedFieldChromeOpacity: number;
    tintEditableFields: boolean;
    editableFieldTintLight: ZebraColor;
    editableFieldTintDark: ZebraColor;
    tintLockedFields: boolean;
    lockedFieldTintLight: ZebraColor;
    lockedFieldTintDark: ZebraColor;
    appTextCase: AppTextCase;
    appFontFamily: string | null;
    appFontSizePx: number | null;
    appFontWeights: FontWeights;
    showFieldNames: boolean;
    globalSuperCompact: boolean;
    globalFitToWidth: boolean;
    globalShowColumnFilters: boolean;
  }) => {
    try {
      await userSettingsService.saveUserSetting('display_preferences', prefs);
    } catch (error) {
      // Silently fail - localStorage is fallback
    }
  };

  const setAppTextCase = async (value: AppTextCase) => {
    const next = normalizeTextCase(value);
    setAppTextCaseState(next);
    localStorage.setItem('appTextCase', next);
    await savePreferences({ ...getCurrentPrefs(), appTextCase: next });
  };

  const setShowTableGridlines = async (enabled: boolean) => {
    setShowTableGridlinesState(enabled);
    localStorage.setItem('showTableGridlines', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      showTableGridlines: enabled,
    });
  };

  const setShowHorizontalGridlines = async (enabled: boolean) => {
    setShowHorizontalGridlinesState(enabled);
    localStorage.setItem('showHorizontalGridlines', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      showHorizontalGridlines: enabled,
    });
  };

  const setShowVerticalGridlines = async (enabled: boolean) => {
    setShowVerticalGridlinesState(enabled);
    localStorage.setItem('showVerticalGridlines', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      showVerticalGridlines: enabled,
    });
  };

  const setShowTableZebraStriping = async (enabled: boolean) => {
    setShowTableZebraStripingState(enabled);
    localStorage.setItem('showTableZebraStriping', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      showTableZebraStriping: enabled,
    });
  };

  const setZebraColorLight = async (color: ZebraColor) => {
    setZebraColorLightState(color);
    localStorage.setItem('zebraColorLight', JSON.stringify(color));
    await savePreferences({
      ...getCurrentPrefs(),
      zebraColorLight: color,
    });
  };

  const setZebraColorDark = async (color: ZebraColor) => {
    setZebraColorDarkState(color);
    localStorage.setItem('zebraColorDark', JSON.stringify(color));
    await savePreferences({
      ...getCurrentPrefs(),
      zebraColorDark: color,
    });
  };

  const setGridlineColorLight = async (color: ZebraColor) => {
    setGridlineColorLightState(color);
    localStorage.setItem('gridlineColorLight', JSON.stringify(color));
    await savePreferences({
      ...getCurrentPrefs(),
      gridlineColorLight: color,
    });
  };

  const setGridlineColorDark = async (color: ZebraColor) => {
    setGridlineColorDarkState(color);
    localStorage.setItem('gridlineColorDark', JSON.stringify(color));
    await savePreferences({
      ...getCurrentPrefs(),
      gridlineColorDark: color,
    });
  };

  const setGroupRowColorLight = async (color: ZebraColor) => {
    setGroupRowColorLightState(color);
    localStorage.setItem('groupRowColorLight', JSON.stringify(color));
    await savePreferences({ ...getCurrentPrefs(), groupRowColorLight: color });
  };
  const setGroupRowColorDark = async (color: ZebraColor) => {
    setGroupRowColorDarkState(color);
    localStorage.setItem('groupRowColorDark', JSON.stringify(color));
    await savePreferences({ ...getCurrentPrefs(), groupRowColorDark: color });
  };

  const setShowColumnWidths = async (enabled: boolean) => {
    setShowColumnWidthsState(enabled);
    localStorage.setItem('showColumnWidths', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      showColumnWidths: enabled,
    });
  };

  const setShowGridHighlight = async (enabled: boolean) => {
    setShowGridHighlightState(enabled);
    localStorage.setItem('showGridHighlight', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      showGridHighlight: enabled,
    });
  };

  const setShowGridLabels = async (enabled: boolean) => {
    setShowGridLabelsState(enabled);
    localStorage.setItem('showGridLabels', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      showGridLabels: enabled,
    });
  };

  const setShowGridBorders = async (enabled: boolean) => {
    setShowGridBordersState(enabled);
    localStorage.setItem('showGridBorders', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      showGridBorders: enabled,
    });
  };

  const setCompactTableMode = async (enabled: boolean) => {
    setCompactTableModeState(enabled);
    localStorage.setItem('compactTableMode', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      compactTableMode: enabled,
    });
  };

  const setCompactOrderDetails = async (enabled: boolean) => {
    setCompactOrderDetailsState(enabled);
    localStorage.setItem('compactOrderDetails', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      compactOrderDetails: enabled,
    });
  };

  const setNavFullSlide = async (enabled: boolean) => {
    setNavFullSlideState(enabled);
    localStorage.setItem('navFullSlide', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      navFullSlide: enabled,
    });
  };

  const setShowInputPlaceholders = async (enabled: boolean) => {
    setShowInputPlaceholdersState(enabled);
    localStorage.setItem('showInputPlaceholders', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      showInputPlaceholders: enabled,
    });
  };

  const setPlainTextLockedFields = async (enabled: boolean) => {
    setPlainTextLockedFieldsState(enabled);
    localStorage.setItem('plainTextLockedFields', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      plainTextLockedFields: enabled,
    });
  };

  const setStyleLockedFieldText = async (enabled: boolean) => {
    setStyleLockedFieldTextState(enabled);
    localStorage.setItem('styleLockedFieldText', enabled.toString());
    await savePreferences({ ...getCurrentPrefs(), styleLockedFieldText: enabled });
  };

  const setLockedFieldTextColorLight = async (color: ZebraColor) => {
    setLockedFieldTextColorLightState(color);
    localStorage.setItem('lockedFieldTextColorLight', JSON.stringify(color));
    await savePreferences({ ...getCurrentPrefs(), lockedFieldTextColorLight: color });
  };

  const setLockedFieldTextColorDark = async (color: ZebraColor) => {
    setLockedFieldTextColorDarkState(color);
    localStorage.setItem('lockedFieldTextColorDark', JSON.stringify(color));
    await savePreferences({ ...getCurrentPrefs(), lockedFieldTextColorDark: color });
  };

  const setLockedFieldTextBold = async (enabled: boolean) => {
    setLockedFieldTextBoldState(enabled);
    localStorage.setItem('lockedFieldTextBold', enabled.toString());
    await savePreferences({ ...getCurrentPrefs(), lockedFieldTextBold: enabled });
  };

  const setLockedFieldTextItalic = async (enabled: boolean) => {
    setLockedFieldTextItalicState(enabled);
    localStorage.setItem('lockedFieldTextItalic', enabled.toString());
    await savePreferences({ ...getCurrentPrefs(), lockedFieldTextItalic: enabled });
  };

  const setLockedFieldChromeOpacity = async (percent: number) => {
    const pct = normalizeOpacityPercent(percent);
    setLockedFieldChromeOpacityState(pct);
    localStorage.setItem('lockedFieldChromeOpacity', String(pct));
    await savePreferences({ ...getCurrentPrefs(), lockedFieldChromeOpacity: pct });
  };

  const setTintEditableFields = async (enabled: boolean) => {
    setTintEditableFieldsState(enabled);
    localStorage.setItem('tintEditableFields', enabled.toString());
    await savePreferences({ ...getCurrentPrefs(), tintEditableFields: enabled });
  };

  const setEditableFieldTintLight = async (color: ZebraColor) => {
    setEditableFieldTintLightState(color);
    localStorage.setItem('editableFieldTintLight', JSON.stringify(color));
    await savePreferences({ ...getCurrentPrefs(), editableFieldTintLight: color });
  };

  const setEditableFieldTintDark = async (color: ZebraColor) => {
    setEditableFieldTintDarkState(color);
    localStorage.setItem('editableFieldTintDark', JSON.stringify(color));
    await savePreferences({ ...getCurrentPrefs(), editableFieldTintDark: color });
  };

  const setTintLockedFields = async (enabled: boolean) => {
    setTintLockedFieldsState(enabled);
    localStorage.setItem('tintLockedFields', enabled.toString());
    await savePreferences({ ...getCurrentPrefs(), tintLockedFields: enabled });
  };

  const setLockedFieldTintLight = async (color: ZebraColor) => {
    setLockedFieldTintLightState(color);
    localStorage.setItem('lockedFieldTintLight', JSON.stringify(color));
    await savePreferences({ ...getCurrentPrefs(), lockedFieldTintLight: color });
  };

  const setLockedFieldTintDark = async (color: ZebraColor) => {
    setLockedFieldTintDarkState(color);
    localStorage.setItem('lockedFieldTintDark', JSON.stringify(color));
    await savePreferences({ ...getCurrentPrefs(), lockedFieldTintDark: color });
  };

  const setAppFontFamily = async (stack: string | null) => {
    setAppFontFamilyState(stack);
    if (stack) localStorage.setItem('appFontFamily', stack);
    else localStorage.removeItem('appFontFamily');
    await savePreferences({ ...getCurrentPrefs(), appFontFamily: stack });
  };

  const setAppFontSizePx = async (px: number | null) => {
    const next = px === null || !Number.isFinite(px)
      ? null
      : Math.max(MIN_FONT_PX, Math.min(MAX_FONT_PX, Math.round(px)));
    setAppFontSizePxState(next);
    if (next === null) localStorage.removeItem('appFontSizePx');
    else localStorage.setItem('appFontSizePx', String(next));
    await savePreferences({ ...getCurrentPrefs(), appFontSizePx: next });
  };

  const setAppFontWeight = async (key: FontWeightKey, weight: number | null) => {
    const next: FontWeights = { ...appFontWeights };
    if (weight === null || !Number.isFinite(weight)) delete next[key];
    else next[key] = clampFontWeight(weight);
    setAppFontWeightsState(next);
    localStorage.setItem('appFontWeights', JSON.stringify(next));
    await savePreferences({ ...getCurrentPrefs(), appFontWeights: next });
  };

  // Family, size and weights in ONE step. The three single setters above each
  // snapshot the OTHER two values from their render's closure, so chaining them
  // in one handler races: the last server write would carry stale copies of the
  // first two and silently undo them.
  const setAppFont = async (next: { family: string | null; sizePx: number | null; weights: FontWeights }) => {
    const family = next.family || null;
    const sizePx = next.sizePx === null || !Number.isFinite(next.sizePx)
      ? null
      : Math.max(MIN_FONT_PX, Math.min(MAX_FONT_PX, Math.round(next.sizePx)));
    const weights = normalizeFontWeights(next.weights);
    setAppFontFamilyState(family);
    setAppFontSizePxState(sizePx);
    setAppFontWeightsState(weights);
    if (family) localStorage.setItem('appFontFamily', family);
    else localStorage.removeItem('appFontFamily');
    if (sizePx === null) localStorage.removeItem('appFontSizePx');
    else localStorage.setItem('appFontSizePx', String(sizePx));
    localStorage.setItem('appFontWeights', JSON.stringify(weights));
    await savePreferences({
      ...getCurrentPrefs(),
      appFontFamily: family,
      appFontSizePx: sizePx,
      appFontWeights: weights,
    });
  };

  const setShowFieldNames = async (enabled: boolean) => {
    setShowFieldNamesState(enabled);
    localStorage.setItem('showFieldNames', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      showFieldNames: enabled,
    });
  };

  const setGlobalSuperCompact = async (enabled: boolean) => {
    setGlobalSuperCompactState(enabled);
    localStorage.setItem('globalSuperCompact', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      globalSuperCompact: enabled,
    });
  };

  const setGlobalFitToWidth = async (enabled: boolean) => {
    setGlobalFitToWidthState(enabled);
    localStorage.setItem('globalFitToWidth', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      globalFitToWidth: enabled,
    });
  };

  const setGlobalShowColumnFilters = async (enabled: boolean) => {
    setGlobalShowColumnFiltersState(enabled);
    localStorage.setItem('globalShowColumnFilters', enabled.toString());
    await savePreferences({
      ...getCurrentPrefs(),
      globalShowColumnFilters: enabled,
    });
  };

  const setHighlightedFieldId = (fieldId: string | null) => {
    setHighlightedFieldIdState(fieldId);
    // Don't persist highlightedFieldId - it's temporary state for highlighting
  };

  // Get inline style for zebra striping
  // Uses CSS variables that are already set on the document root
  // and automatically switch between light/dark based on the dark mode class
  const getZebraStyle = (): React.CSSProperties => {
    return {
      backgroundColor: 'rgb(var(--zebra-stripe-light))',
    };
  };

  // Get CSS class for zebra striping that respects dark mode
  const getZebraClass = (): string => {
    return 'zebra-stripe-row';
  };

  return (
    <DisplayPreferencesContext.Provider
      value={{
        showTableGridlines,
        setShowTableGridlines,
        showHorizontalGridlines,
        setShowHorizontalGridlines,
        showVerticalGridlines,
        setShowVerticalGridlines,
        showTableZebraStriping,
        setShowTableZebraStriping,
        zebraColorLight,
        setZebraColorLight,
        zebraColorDark,
        setZebraColorDark,
        gridlineColorLight,
        setGridlineColorLight,
        gridlineColorDark,
        setGridlineColorDark,
        groupRowColorLight,
        setGroupRowColorLight,
        groupRowColorDark,
        setGroupRowColorDark,
        getZebraStyle,
        getZebraClass,
        isLoading,
        showColumnWidths,
        setShowColumnWidths,
        showGridHighlight,
        setShowGridHighlight,
        showGridLabels,
        setShowGridLabels,
        showGridBorders,
        setShowGridBorders,
        compactTableMode,
        setCompactTableMode,
        compactOrderDetails,
        setCompactOrderDetails,
        navFullSlide,
        setNavFullSlide,
        showInputPlaceholders,
        setShowInputPlaceholders,
        plainTextLockedFields,
        setPlainTextLockedFields,
        styleLockedFieldText,
        setStyleLockedFieldText,
        lockedFieldTextColorLight,
        setLockedFieldTextColorLight,
        lockedFieldTextColorDark,
        setLockedFieldTextColorDark,
        lockedFieldTextBold,
        setLockedFieldTextBold,
        lockedFieldTextItalic,
        setLockedFieldTextItalic,
        lockedFieldChromeOpacity,
        setLockedFieldChromeOpacity,
        tintEditableFields,
        setTintEditableFields,
        editableFieldTintLight,
        setEditableFieldTintLight,
        editableFieldTintDark,
        setEditableFieldTintDark,
        tintLockedFields,
        setTintLockedFields,
        lockedFieldTintLight,
        setLockedFieldTintLight,
        lockedFieldTintDark,
        setLockedFieldTintDark,
        appTextCase,
        setAppTextCase,
        appFontFamily,
        setAppFontFamily,
        appFontSizePx,
        setAppFontSizePx,
        appFontWeights,
        setAppFontWeight,
        setAppFont,
        showFieldNames,
        setShowFieldNames,
        highlightedFieldId,
        setHighlightedFieldId,
        globalSuperCompact,
        setGlobalSuperCompact,
        globalFitToWidth,
        setGlobalFitToWidth,
        globalShowColumnFilters,
        setGlobalShowColumnFilters,
      }}
    >
      {children}
    </DisplayPreferencesContext.Provider>
  );
};

/**
 * Markers the shared field components put on whatever element draws their box
 * (wrapper div, trigger button, or the input itself) — no selector can reach
 * "the ancestor that draws the box", so the component has to say which element
 * that is. Locked and editable are separate markers because the two states are
 * styled independently (a locked box can be flattened, restyled, faded or
 * tinted; an editable box can only be tinted).
 *
 * WHICH treatment applies is decided by the root class on <html>
 * (`plain-locked-fields`, `styled-locked-fields`, `faded-locked-fields`,
 * `tint-locked-fields`, `tint-editable-fields`) — the same way
 * `.zebra-stripe-row` is gated by `html.dark`. The marker only ever says "this
 * is the element", never "this is the styling", so the components stay
 * agnostic about the individual options.
 */
export const FIELD_SHELL_LOCKED_CLASS = 'field-shell-locked';
export const FIELD_SHELL_EDITABLE_CLASS = 'field-shell-editable';

export interface FieldShellDisplay {
  /** Flatten the shell and drop picker affordances (icons, chevrons). Every
   *  other option keeps the field's chrome, so only this one changes markup. */
  plain: boolean;
  /** Marker class for the element that draws the box / owns the value. */
  className: string;
}

/**
 * What the field display preferences mean for one field, given whether it's
 * locked (disabled / read-only) or editable.
 *
 * Deliberately tolerant of a missing provider — shared field components are
 * rendered bare in plenty of tests, and a hard throw there would be a
 * regression, not a signal. No provider => today's untouched look.
 */
export const useFieldShellDisplay = (locked: boolean): FieldShellDisplay => {
  const context = useContext(DisplayPreferencesContext);

  if (!locked) {
    return {
      plain: false,
      className: context?.tintEditableFields ? FIELD_SHELL_EDITABLE_CLASS : '',
    };
  }

  const plain = Boolean(context?.plainTextLockedFields);
  const anyLockedOption = plain
    || Boolean(context?.styleLockedFieldText)
    || Boolean(context?.tintLockedFields)
    || (context?.lockedFieldChromeOpacity ?? 100) < 100;
  return { plain, className: anyLockedOption ? FIELD_SHELL_LOCKED_CLASS : '' };
};

export const useDisplayPreferences = (): DisplayPreferencesContextType => {
  const context = useContext(DisplayPreferencesContext);
  if (!context) {
    throw new Error('useDisplayPreferences must be used within a DisplayPreferencesProvider');
  }
  return context;
};
